// TESSERAX — formation « Crystal Resonance » de la VITRINE (accueil / écran de fin).
// PUR (sans THREE ni DOM) et déterministe : à partir d'une graine, génère un
// artefact cristallin différent CHAQUE JOUR — l'écran d'accueil n'est plus une
// formation figée mais un « artefact d'une civilisation supérieure » qui se
// renouvelle (cf. DA : Sacred Sci-Fi Geometry), calé sur la graine du défi du
// jour. Purement visuel, hors-partie → aucun impact sur le jeu ni le déterminisme
// d'une run. La couleur des cubes reste décidée par leur hauteur dans main.ts.
//
// La formation est TOUJOURS à symétrie D4 (les 8 isométries du carré) pour se
// lire comme un objet intentionnel, jamais comme du bruit : la hauteur d'une
// colonne ne dépend que de sa distance au centre (anneau) et de son « bras »
// (cf. ring/arm ci-dessous), partagés par toutes les colonnes symétriques.
import { GRID, HEIGHT } from './constants';
import { mulberry32 } from './rng';

// Plafond de hauteur de la vitrine : on laisse de l'air sous le sommet du puits
// (HEIGHT) pour que la couronne ambrée « respire » et que la caméra la cadre bien.
const CROWN = Math.min(HEIGHT - 2, 12);

// Hache une graine alphanumérique en entier 32 bits (FNV-1a) → mulberry32.
function hashSeed(seed: string | number): number {
  const s = String(seed);
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}

export const ARCHETYPES = ['spire', 'crown', 'ziggurat', 'cross', 'lattice', 'helix'] as const;
export type Archetype = typeof ARCHETYPES[number];

// Hauteur d'une colonne selon son anneau (Chebyshev) et son bras (min des écarts),
// par archétype. r ∈ [0, R], a ∈ [0, r]. `lift` est un relief par-anneau (seedé,
// identique pour tout l'anneau → symétrie préservée). Renvoie une hauteur brute
// (clampée/arrondie par l'appelant).
function columnHeight(arch: Archetype, r: number, a: number, R: number, top: number, lift: number): number {
  const rN = R > 0 ? r / R : 0;                 // 0 au centre → 1 au bord
  const isCorner = a === r;                      // sur la diagonale (coin de l'anneau)
  const isAxis = a === 0;                        // sur un axe central (+,×)
  switch (arch) {
    case 'spire':                               // flèche centrale, décroît vers le bord
      return top * (1 - rN * 0.82) + lift;
    case 'crown':                               // caldeira : centre creux, rempart à mi-distance
      return top * (0.34 + 0.66 * Math.exp(-Math.pow((r - R * 0.62) / (R * 0.42 + 0.5), 2))) + lift;
    case 'ziggurat':                            // plateaux concentriques en marches
      return top - r * Math.max(1, Math.round(top / (R + 1))) + lift;
    case 'cross':                               // arêtes sur les axes hautes, coins bas
      return (isAxis ? top * (1 - rN * 0.5) : top * (0.34 + 0.32 * (1 - rN))) + lift;
    case 'lattice':                             // piliers d'angle + remplissage central bas
      return (isCorner && r > 0 ? top * (0.72 + 0.28 * rN) : top * (0.5 - rN * 0.24)) + lift;
    case 'helix':                               // crête qui s'élève par anneau (spirale lue en hauteur)
      return top * (0.4 + 0.6 * (a / (r + 1))) + lift;
  }
}

// Carte de hauteurs GRID×GRID (heights[x][z], entiers ≥ 0), à symétrie D4 garantie.
export function showcaseHeights(seed: string | number): number[][] {
  const rng = mulberry32(hashSeed(seed));
  const arch = ARCHETYPES[(rng() * ARCHETYPES.length) | 0];
  const C = (GRID - 1) / 2;                      // centre (3 pour GRID=7)
  const R = Math.ceil(C);                        // anneau maximal
  // Sommet visé : assez haut pour atteindre la couronne ambrée (« wow »), avec
  // une variation quotidienne contenue.
  const top = CROWN * (0.78 + rng() * 0.22);
  // Relief par-anneau (seedé une fois par anneau → garde la symétrie).
  const lift: number[] = [];
  for (let r = 0; r <= R; r++) lift[r] = (rng() - 0.4) * 2.0;

  const heights: number[][] = [];
  for (let x = 0; x < GRID; x++) {
    heights[x] = [];
    for (let z = 0; z < GRID; z++) {
      const dx = Math.abs(x - C), dz = Math.abs(z - C);
      const r = Math.round(Math.max(dx, dz)), a = Math.round(Math.min(dx, dz));
      let h = columnHeight(arch, r, a, R, top, lift[r]);
      h = Math.round(h);
      // Bornes : jamais négatif, jamais au-dessus de la couronne. On garantit un
      // socle (≥1) sur l'anneau extérieur pour une empreinte lisible au sol.
      if (r >= R) h = Math.max(h, 1);
      heights[x][z] = Math.max(0, Math.min(CROWN, h));
    }
  }
  return heights;
}

// Liste plate des cubes [x, y, z] de la formation (y de 0 à hauteur-1 par colonne).
export function showcaseFormation(seed: string | number): number[][] {
  const heights = showcaseHeights(seed);
  const cells: number[][] = [];
  for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) {
    const h = heights[x][z];
    for (let y = 0; y < h; y++) cells.push([x, y, z]);
  }
  return cells;
}
